import { useEffect, useState } from "react";
import { EmailLink } from "./EmailLink";
import { Link } from "react-router-dom";
import { Chrome, Menu, X } from "lucide-react";
import { cn } from "../lib/cn";
import { storeUrl } from "../lib/links";
import { home } from "../content/home";

const BASE = __BASE_PATH__.replace(/\/+$/, "");
// Anchors carry the base path so they work from the legal pages too.
const NAV_LINKS = home.nav.links.map(({ label, href }) => ({ label, href: `${BASE}/${href}` }));
const TALK_EMAIL = "hello@intakall.io";
const SCROLL_THRESHOLD = 8;
const BAR_MIDDLE = 36; // half the 72px bar
// Plain links get a padded pill on hover, so every clickable thing in the bar answers the pointer.
const hoverPill = "rounded-lg px-3 py-1.5 transition-colors duration-150 hover:bg-char/[0.07]";
const onDarkHover = "hover:bg-paper-white/10";

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-paper";
// Pressing a button "lands" it: the hard shadow shrinks as the button moves into it.
const pressable =
  "border border-char shadow-offset-sm transition-[transform,box-shadow,padding] duration-150 hover:-translate-x-px hover:translate-y-px hover:shadow-[-2px_3px_0_0_#262524] active:-translate-x-[3px] active:translate-y-[4px] active:shadow-none";

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [onDark, setOnDark] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > SCROLL_THRESHOLD);
      // A section marked data-nav-dark (the security band) under the bar's middle flips the bar to dark.
      const darkUnderBar = Array.from(document.querySelectorAll("[data-nav-dark]")).some((el) => {
        const { top, bottom } = el.getBoundingClientRect();
        return top < BAR_MIDDLE && bottom > BAR_MIDDLE;
      });
      setOnDark(darkUnderBar);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <header data-scrolled={scrolled} data-on-dark={onDark} className="sticky top-0 z-50 font-sans">
      {/* No line, no shadow: the bar is the page itself. Once scrolled, a frost fades in behind the links and
          fades out downward, so content dissolves under it instead of meeting an edge. */}
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-x-0 top-0 h-24 backdrop-blur-md transition-[opacity,background-color] duration-200 [mask-image:linear-gradient(to_bottom,black_55%,transparent)]",
          onDark ? "bg-char/80" : "bg-paper-white/80",
          scrolled ? "opacity-100" : "opacity-0",
        )}
      />
      <div className="relative mx-auto flex h-[72px] max-w-6xl items-center justify-between px-4 sm:px-6">
        <Link to="/" className={cn("-ml-3 transition-colors", onDark ? "text-paper-white" : "text-char", hoverPill, onDark && onDarkHover, focusRing)}>
          <span className="inline-flex items-center gap-2.5">
            <img
              src={`${BASE}/mark.svg`}
              alt=""
              aria-hidden="true"
              width={26}
              height={26}
              className="h-[26px] w-[26px]"
            />
            <span className="font-sans text-[24px] font-semibold tracking-[-0.02em]">Intakall</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-2 md:flex">
          {NAV_LINKS.map(({ label, href }) => (
            <a
              key={href}
              href={href}
              className={cn(
                "text-[15px] font-medium transition-colors",
                onDark ? "text-paper-white/75 hover:text-paper-white" : "text-char/75 hover:text-char",
                hoverPill,
                onDark && onDarkHover,
                focusRing,
              )}
            >
              {label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <EmailLink
            email={TALK_EMAIL}
            align="right"
            className={cn(
              "inline-flex h-10 items-center rounded-[10px] bg-paper-raised px-4 text-sm font-semibold text-char hover:px-5",
              pressable,
              focusRing,
            )}
          >
            {home.nav.talk}
          </EmailLink>
          <a
            href={storeUrl("navbar")}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "inline-flex h-10 items-center gap-2 rounded-[10px] bg-brand-600 px-4 text-sm font-semibold text-white hover:px-5",
              pressable,
              focusRing,
            )}
          >
            <Chrome className="h-4 w-4" aria-hidden="true" />
            {home.nav.install}
          </a>
        </div>

        <button
          type="button"
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "inline-flex h-10 w-10 items-center justify-center rounded-[10px] transition-colors md:hidden",
            onDark && !open ? "text-paper-white hover:bg-paper-white/10" : "text-char hover:bg-paper-raised",
            focusRing,
          )}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          role="dialog"
          aria-label="Mobile menu"
          className="relative border-t border-char/15 bg-paper px-6 py-6 md:hidden"
        >
          <nav className="flex flex-col gap-4">
            {NAV_LINKS.map(({ label, href }) => (
              <a key={href} href={href} onClick={close} className={cn("-mx-3 font-serif text-2xl text-char", hoverPill)}>
                {label}
              </a>
            ))}
          </nav>
          <div className="mt-6 flex flex-col gap-3 border-t border-char/15 pt-6">
            <EmailLink
              email={TALK_EMAIL}
              onOpenApp={close}
              wrapperClassName="flex flex-col"
              className={cn(
                "inline-flex h-11 items-center justify-center rounded-[10px] bg-paper-raised text-sm font-semibold text-char",
                pressable,
              )}
            >
              {home.nav.talk}
            </EmailLink>
            <a
              href={storeUrl("navbar")}
              target="_blank"
              rel="noopener noreferrer"
              onClick={close}
              className={cn(
                "inline-flex h-11 items-center justify-center gap-2 rounded-[10px] bg-brand-600 text-sm font-semibold text-white",
                pressable,
              )}
            >
              <Chrome className="h-4 w-4" aria-hidden="true" />
              {home.nav.install}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
